import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../../assets/images/logo.png";
import { detectPlatform } from "../AppDownloadBanner";
import { isBlankFile, lawyerAuth, lawyerService } from "../../services/lawyerApi";
import socketService from "../../services/SocketService";
import { Modal } from "./LawyerUi";
import LawyerCallManager from "./call/LawyerCallManager";
import SupportCallManager from "../support/SupportCallManager";
import "./lawyer.css";

const NAV_ITEMS = [
  { to: "/lawyer/dashboard", label: "Home", icon: "bi-house-door-fill" },
  { to: "/lawyer/call-history", label: "Call History", icon: "bi-telephone-inbound-fill" },
  { to: "/lawyer/chat", label: "Chat", icon: "bi-chat-dots-fill", badgeKey: "chat_unread" },
  { to: "/lawyer/settings", label: "Settings", icon: "bi-gear-fill" },
  { to: "/lawyer/profile", label: "Profile", icon: "bi-person-fill" },
];

const DASHBOARD = "/lawyer/dashboard";

// Just registered, not yet approved by admin: they can look around the
// dashboard, but every action and every other page opens this notice instead.
const isPending = (profile) => Number(profile?.is_approved) === 0;

const SERVICES_PAGE = "/lawyer/business-services";
const PAYOUT_PAGE = "/lawyer/payout-method";
const GET_APP_PAGE = "/lawyer/get-app";

// On a phone or tablet the portal is a stopgap: they get the app first,
// and can carry on here once they've signed in to it.
const onMobile = !!detectPlatform();
const hasMobileApp = (p) => !!p?.mobile_app_used_at;

const listOf = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value ? [value] : [];
  }
};

// Same bar as the Partner portal: a price, at least one service and one image.
const hasBusinessProfile = (p) =>
  Number(p?.weekly_price) >= 1 &&
  listOf(p?.services).some((s) => (typeof s === "string" ? s : s?.title || "").trim()) &&
  listOf(p?.images).some((u) => !isBlankFile(u));

// Somewhere to be paid: PayPal, an IBAN, or a finished Stripe onboarding.
const hasPayout = (p) =>
  !!(p?.bank_details?.paypal || p?.bank_details?.iban || p?.stripe_onboarding_complete);

// Every lawyer, approved or not, finishes setup first: services, then payout,
// then (on mobile) the app.
const setupStep = (p) => {
  if (!hasBusinessProfile(p)) return { to: SERVICES_PAGE, message: "Please add your business services to continue." };
  if (!hasPayout(p)) return { to: PAYOUT_PAGE, message: "Please add your payout details (PayPal or bank) to continue." };
  if (onMobile && !hasMobileApp(p)) return { to: GET_APP_PAGE, message: "Please download the mobile app to continue." };
  return null;
};

const PendingApprovalModal = ({ open, onClose, name }) => (
  <Modal
    open={open}
    onClose={onClose}
    className="lwp-pending-modal"
    footer={
      <button type="button" className="lwp-btn" onClick={onClose}>
        OK
      </button>
    }
  >
    <div className="lwp-pending">
      <span className="lwp-pending-icon">
        <i className="bi bi-hourglass-split" />
      </span>
      <h2>Account under approval</h2>
      <p>
        Thank you for registering{name ? `, ${name}` : ""}. Our team is reviewing your details and documents.
        You&apos;ll receive an email once your account is approved — then you can start taking calls and clients here.
      </p>
    </div>
  </Modal>
);

// Shell for every signed-in lawyer page: sidebar on desktop, floating bottom
// bar on mobile. The profile is loaded once here and shared via Outlet context.
const LawyerLayout = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(lawyerAuth.getUser());
  // The cached profile may predate services/bank details — only gate on a fresh one.
  const [profileFresh, setProfileFresh] = useState(false);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await lawyerService.getProfile();
      if (res?.data?.status) {
        setProfile(res.data.data);
        lawyerAuth.setUser(res.data.data);
        setProfileFresh(true);
      }
    } catch {
      // 401s are handled by the interceptor; keep the cached profile otherwise.
    }
  }, []);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const pending = isPending(profile);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const { pathname } = useLocation();

  // Setup comes before the approval lock, so a pending lawyer can still fill
  // in services and payout; only once that's done are they held on the dashboard.
  const setup = profileFresh ? setupStep(profile) : null;
  const offSetup = !!setup && pathname !== setup.to;
  const locked = pending && !setup;
  const offDashboard = locked && pathname !== DASHBOARD;

  // Toast once per step, not on every page they try.
  const setupMessage = setup?.message;
  useEffect(() => {
    if (setupMessage) toast.info(setupMessage, { toastId: setupMessage });
  }, [setupMessage]);

  // Landed on another page by URL — send them home and say why.
  useEffect(() => {
    if (offDashboard) setNoticeOpen(true);
  }, [offDashboard]);

  // Nav: Home stays usable, every other page is held back.
  const guardNav = (to) => (e) => {
    if (setup) {
      if (to === setup.to) return;
      e.preventDefault();
      toast.info(setup.message, { toastId: setup.message });
      return;
    }
    if (!locked || to === DASHBOARD) return;
    e.preventDefault();
    setNoticeOpen(true);
  };

  // Dashboard: swallow any link, button or switch before it acts.
  const guardActions = (e) => {
    if (!locked || !e.target.closest("a, button, input, label, [role='button']")) return;
    e.preventDefault();
    e.stopPropagation();
    setNoticeOpen(true);
  };

  // While pending, re-check now and then so the portal unlocks once approved.
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(refreshProfile, 60000);
    window.addEventListener("focus", refreshProfile);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refreshProfile);
    };
  }, [pending, refreshProfile]);

  // Register this lawyer with the socket server (and again after every
  // reconnect) so chat messages, read receipts and presence reach this tab.
  // A pending lawyer stays off the socket: not shown online, never rung.
  const lawyerId = pending ? null : profile?.id;
  const lawyerName = profile?.name;
  useEffect(() => {
    if (!lawyerId) return undefined;
    const announce = () => socketService.emit("lawyer-connected", { id: lawyerId, name: lawyerName });
    announce();
    const offConnect = socketService.subscribe("connect", announce);
    // Keeps the Chat badge current; the open conversation marks its own reads.
    const offMessage = socketService.subscribe("receive-message", () => setTimeout(refreshProfile, 800));
    return () => {
      offConnect();
      offMessage();
    };
  }, [lawyerId, lawyerName, refreshProfile]);

  const inChat = !!useMatch("/lawyer/chat/:chatId");
  const inServiceChat = !!useMatch("/lawyer/clients/:chatId");
  const inConversation = inChat || inServiceChat;

  const logout = async () => {
    try {
      await lawyerService.logout();
    } catch {
      // Clear the local session even if the server call fails.
    }
    lawyerAuth.clear();
    toast.success("Logged out successfully");
    navigate("/lawyer/login", { replace: true });
  };

  return (
    <div className={`lwp lwp-shell${inConversation ? " lwp-in-conversation" : ""}`}>
      <aside className="lwp-sidebar">
        <img src={logo} alt="Legal Platform" className="lwp-sidebar-logo" />
        <nav className="lwp-sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className="lwp-sidebar-link" onClick={guardNav(item.to)}>
              <i className={`bi ${item.icon}`} />
              <span>{item.label}</span>
              {item.badgeKey && profile?.[item.badgeKey] > 0 && (
                <span className="lwp-count">{profile[item.badgeKey]}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <button type="button" className="lwp-sidebar-link lwp-logout" onClick={logout}>
          <i className="bi bi-box-arrow-right" />
          <span>Logout</span>
        </button>
      </aside>

      <main className="lwp-main" onClickCapture={guardActions}>
        {offDashboard ? (
          <Navigate to={DASHBOARD} replace />
        ) : offSetup ? (
          <Navigate to={setup.to} replace />
        ) : (
          <Outlet context={{ profile, setProfile, refreshProfile, logout }} />
        )}
      </main>

      <PendingApprovalModal open={locked && noticeOpen} onClose={() => setNoticeOpen(false)} name={profile?.name} />

      {/* Rings over any page and runs the video call once accepted. */}
      {!pending && <LawyerCallManager profile={profile} />}
      {/* Voice/video calls with the support team, placed from the support chat. */}
      <SupportCallManager side="lawyer" api={lawyerService} auth={lawyerAuth} profile={profile} />

      <nav className="lwp-bottom-nav">
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.to} to={item.to} className="lwp-bottom-link" onClick={guardNav(item.to)}>
            <span className="lwp-bottom-icon">
              <i className={`bi ${item.icon}`} />
              {item.badgeKey && profile?.[item.badgeKey] > 0 && (
                <span className="lwp-count">{profile[item.badgeKey]}</span>
              )}
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default LawyerLayout;
